"use client";

/**
 * components/push-prompt-banner.tsx
 *
 * Dismissible banner shown to authenticated users who have push notifications
 * enabled (notifyPush=true) but no subscription stored yet.
 *
 * On mount it checks:
 *  1. localStorage — if they already dismissed or subscribed from this device, do nothing
 *  2. Browser permission — if already "granted", go straight to subscribe silently
 *  3. Otherwise show the banner so they can tap to enable
 *
 * Once subscribed or dismissed, sets localStorage so it never shows again on this device.
 */

import { useEffect, useState, useCallback } from "react";
import { clog } from "@/lib/client-log";

const DISMISSED_KEY = "bnc-push-prompt-dismissed";

interface Props {
  vapidPublicKey: string;
}

type State = "hidden" | "visible" | "loading" | "done";

// Reused from PushToggle — converts base64url VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length) as Uint8Array<ArrayBuffer>;
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function doSubscribe(vapidPublicKey: string): Promise<"subscribed" | "denied" | "error"> {
  try {
    const registration = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      clog.warn("push-prompt", "notification permission denied");
      return "denied";
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });

    const res = await fetch("/api/push/subscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subscription: subscription.toJSON() }),
    });

    if (!res.ok) {
      clog.error("push-prompt", "subscribe API error", { status: res.status });
      return "error";
    }

    clog.info("push-prompt", "push subscription saved successfully");
    return "subscribed";
  } catch (err) {
    clog.error("push-prompt", "doSubscribe threw", {
      error: err instanceof Error ? err.message : String(err),
    });
    return "error";
  }
}

export default function PushPromptBanner({ vapidPublicKey }: Props) {
  const [state, setState] = useState<State>("hidden");

  useEffect(() => {
    // Guard: browser support
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      clog.debug("push-prompt", "push not supported on this browser — skipping");
      return;
    }

    // Guard: already dismissed or handled on this device
    if (localStorage.getItem(DISMISSED_KEY) === "1") {
      return;
    }

    const permission = Notification.permission;

    if (permission === "denied") {
      // Can't do anything — user blocked us in browser settings
      clog.debug("push-prompt", "notification permission is denied — skipping banner");
      return;
    }

    if (permission === "granted") {
      // Permission already granted — subscribe silently without showing a banner
      clog.info("push-prompt", "permission already granted — subscribing silently");
      doSubscribe(vapidPublicKey).then((result) => {
        if (result === "subscribed") {
          localStorage.setItem(DISMISSED_KEY, "1");
        }
      });
      return;
    }

    // permission === "default" — need to ask. Show the banner.
    setState("visible");
    clog.info("push-prompt", "showing push prompt banner");
  }, [vapidPublicKey]);

  const handleEnable = useCallback(async () => {
    setState("loading");
    clog.info("push-prompt", "user tapped Enable Notifications");
    const result = await doSubscribe(vapidPublicKey);

    if (result === "subscribed") {
      localStorage.setItem(DISMISSED_KEY, "1");
      setState("done");
      // Auto-hide after a moment
      setTimeout(() => setState("hidden"), 2500);
    } else if (result === "denied") {
      // Dismissed permanently — they said no to the browser prompt
      localStorage.setItem(DISMISSED_KEY, "1");
      setState("hidden");
    } else {
      // Error — let them retry, but don't nag forever
      setState("visible");
    }
  }, [vapidPublicKey]);

  const handleDismiss = useCallback(() => {
    clog.info("push-prompt", "user dismissed push prompt banner");
    localStorage.setItem(DISMISSED_KEY, "1");
    setState("hidden");
  }, []);

  if (state === "hidden") return null;

  if (state === "done") {
    return (
      <div className="relative z-40 bg-green-700 text-white px-4 py-2 text-sm shadow-md text-center font-bold">
        🔔 Push notifications enabled!
      </div>
    );
  }

  return (
    <div className="relative z-40 bg-purple-800 text-white px-4 py-3 text-sm shadow-lg">
      <div className="max-w-lg mx-auto flex items-center gap-3">
        <div className="text-xl flex-shrink-0">🔔</div>

        <div className="flex-1 leading-snug">
          <span className="font-bold text-white">Get notified when it's your turn to vote.</span>{" "}
          <span className="text-purple-300">Enable push notifications to stay in the game.</span>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleEnable}
            disabled={state === "loading"}
            className="rounded-lg bg-yellow-400 hover:bg-yellow-300 text-purple-900 font-black text-xs px-3 py-1.5 transition disabled:opacity-60 select-none"
            style={{ WebkitUserSelect: "none", userSelect: "none" }}
          >
            {state === "loading" ? "Enabling…" : "Enable"}
          </button>
          <button
            onClick={handleDismiss}
            className="text-purple-400 hover:text-white text-lg leading-none select-none"
            style={{ WebkitUserSelect: "none", userSelect: "none" }}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
